// ---------------------------------------------------------------------------
// FieldDetectionModal — AI-detect farm fields from satellite imagery.
//
// Flow: capture a Mapbox satellite image of the farm's deal parcels → send it
// (with exact geo-bounds) to POST /api/farms/{id}/detect-fields → Claude vision
// outlines the distinct fields → REVIEW the suggested boundaries on an editable
// map (drag vertices, draw new fields, delete) → bulk-create the accepted ones
// as Fields (POST /api/fields, which already accepts `geometry`).
//
// The Mapbox token is client-side only (REACT_APP_MAPBOX_TOKEN), so imagery is
// captured here in the browser; the backend never needs a Mapbox token.
//
// NOTE: a vision model's outlines are approximate — they are fully editable
// suggestions to review, not survey-grade boundaries.
// ---------------------------------------------------------------------------
import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, Modal, Input, Spinner } from '../ui';
import { MAPBOX_TOKEN } from './ParcelsMapTab';
import { FieldBoundaryEditor } from './FieldBoundaryEditor';
import { bboxOfFC, clusterParcels, polygonAcres, polygonCentroid } from '../../utils/fieldGeo';
import { num } from '../../constants/farm';

// Web-Mercator latitude helper — used only to size the captured image so its
// pixel aspect ratio equals the bbox's in Mercator (so image edges = bbox edges).
const latToMercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2));
const clampLat = (v) => Math.max(-85, Math.min(85, v));
const clampLng = (v) => Math.max(-180, Math.min(180, v));

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = () => reject(new Error('Could not read the captured image.'));
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.readAsDataURL(blob);
});

const acresStr = (geometry) => {
  const a = Math.round(polygonAcres(geometry) * 100) / 100;
  return a ? String(a) : '';
};

// Per-source display (chip label + color) for the review UI.
const SOURCE_META = {
  claude:   { label: 'Claude',   color: '#6366f1' },
  provider: { label: 'Provider', color: '#0ea5e9' },
  model:    { label: 'Model',    color: '#10b981' },
  manual:   { label: 'Manual',   color: '#94a3b8' },
};
const sourceMeta = (key) => SOURCE_META[key] || { label: key || '—', color: '#94a3b8' };

// Project a [lng,lat] to pixel [x,y] within a W×H image of `bbox` (longitude
// linear; latitude Web-Mercator-correct — the inverse of the endpoint's
// pixelToLngLat). Origin top-left.
const lngLatToPixel = (lng, lat, bbox, W, H) => {
  const x = ((lng - bbox.west) / ((bbox.east - bbox.west) || 1e-9)) * W;
  const mercN = latToMercY(bbox.north), mercS = latToMercY(bbox.south);
  const y = ((mercN - latToMercY(lat)) / ((mercN - mercS) || 1e-9)) * H;
  return [x, y];
};

// Draw the deal's parcel outlines onto the captured satellite image (bright
// magenta) so the vision model can align field boundaries to the real parcel
// lines and stay inside them — the single strongest prior for field edges in
// row-crop country. Best-effort: on any failure returns the original blob.
// Re-encodes as JPEG (photographic imagery) to keep the base64 well under the
// ~5 MB per-image cap now that we composite.
async function overlayParcels(blob, bbox, W, H, parcelsFC) {
  try {
    if (!parcelsFC?.features?.length || typeof document === 'undefined') return blob;
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.drawImage(bmp, 0, 0, W, H);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(255,0,255,0.85)'; // magenta — rare in satellite imagery
    ctx.lineJoin = 'round';
    for (const f of parcelsFC.features) {
      const g = f.geometry;
      const rings = g?.type === 'Polygon' ? g.coordinates
        : g?.type === 'MultiPolygon' ? g.coordinates.flat() : [];
      for (const ring of rings) {
        if (!ring?.length) continue;
        ctx.beginPath();
        ring.forEach((pt, i) => {
          const [x, y] = lngLatToPixel(pt[0], pt[1], bbox, W, H);
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.stroke();
      }
    }
    const out = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
    return out || blob;
  } catch { return blob; }
}

export const FieldDetectionModal = ({ farm, onClose, onCreated }) => {
  const [phase, setPhase]   = useState('loading'); // loading | review | error | saving
  const [errMsg, setErrMsg] = useState('');
  const [items, setItems]   = useState([]);        // [{ id, geometry, include, name, acres, confidence, centroidLat, centroidLng, source }]
  const [saveMsg, setSaveMsg] = useState('');
  const [loadMsg, setLoadMsg] = useState('');      // progress during capture/detect
  const [seed, setSeed]     = useState([]);        // initial features for the editor (set once, at review start)
  const [availableSources, setAvailableSources] = useState([]); // [{ key, label, enabled }]
  const [activeSource, setActiveSource] = useState('auto');      // which source this run used
  const [reviewNote, setReviewNote] = useState('');             // non-fatal note shown atop review

  const parcelFCRef = useRef(null);
  const clustersRef = useRef(null);
  const aliveRef    = useRef(true);

  // Capture a Mapbox satellite image of one bbox and run detection on it,
  // returning the (parcel-clipped) suggestions. `raw` is the un-padded bbox;
  // `source` ('auto'|'claude'|'provider'|'model') asks a specific source to run.
  const detectBbox = async (raw, source = 'auto') => {
    // Pad the bbox ~8% so edge fields aren't clipped.
    const dw = (raw.east - raw.west) * 0.08, dh = (raw.north - raw.south) * 0.08;
    const bbox = {
      west:  clampLng(raw.west - dw),  east:  clampLng(raw.east + dw),
      south: clampLat(raw.south - dh), north: clampLat(raw.north + dh),
    };

    // Size the image so its pixel aspect == the bbox aspect in Mercator.
    const mercW = (bbox.east - bbox.west) * Math.PI / 180;
    const mercH = latToMercY(bbox.north) - latToMercY(bbox.south);
    const aspect = mercW / (mercH || 1e-9);
    const MAXD = 1280; // Mapbox static max per side — use it all for resolution
    let W = MAXD, H = MAXD;
    if (aspect >= 1) H = Math.round(MAXD / aspect); else W = Math.round(MAXD * aspect);
    W = Math.max(64, Math.min(1280, W)); H = Math.max(64, Math.min(1280, H));

    const url = `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/` +
      `[${bbox.west},${bbox.south},${bbox.east},${bbox.north}]/${W}x${H}` +
      `?access_token=${MAPBOX_TOKEN}&attribution=false&logo=false&padding=0`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Could not fetch satellite imagery (${resp.status}).`);
    const rawBlob = await resp.blob();
    // Overlay the parcel outlines so the model can align to real edges.
    const blob = await overlayParcels(rawBlob, bbox, W, H, parcelFCRef.current);
    const imageBase64 = await blobToBase64(blob);

    // Vision calls are slow — override the 10s default timeout.
    const res = await api.post(`farms/${farm.farmID}/detect-fields`, {
      imageBase64, mimeType: blob.type || 'image/jpeg', bbox, source,
    }, { timeout: 120000 });
    return { suggestions: res.suggestions || [], warning: res.warning, availableSources: res.availableSources };
  };

  // ── Capture → detect pipeline. `source` = 'auto' | 'claude' | 'provider' |
  // 'model'. Re-runnable so the user can ask a specific/next source to try. ──
  const runDetection = async (source = 'auto') => {
    setActiveSource(source);
    setReviewNote('');
    setErrMsg('');
    setLoadMsg('');
    setPhase('loading');
    try {
      if (!MAPBOX_TOKEN) throw new Error('Mapbox is not configured (REACT_APP_MAPBOX_TOKEN). Field detection needs satellite imagery.');
      if (!farm.dealID)  throw new Error('This farm is not linked to a deal, so it has no parcels to analyze.');

      // Parcel geometry (fetched once, reused across re-runs).
      let fc = parcelFCRef.current;
      if (!fc) {
        fc = await api.get(`parcel-map?dealId=${farm.dealID}`);
        if (!aliveRef.current) return;
        parcelFCRef.current = fc;
      }
      if (!bboxOfFC(fc)) throw new Error('No parcel boundaries found for this deal. Add/enrich parcels with GIS geometry first (Parcels tab), then try again.');

      // Cluster the parcels — dispersed parcel groups (tracts miles apart) are
      // imaged SEPARATELY so each is captured at a resolvable zoom.
      let clusters = clustersRef.current;
      if (!clusters) { clusters = clusterParcels(fc); clustersRef.current = clusters; }
      const MAX_CLUSTERS = 12;
      const use = clusters.slice(0, MAX_CLUSTERS);

      // Detect within each cluster, aggregating suggestions. One failing cluster
      // doesn't sink the rest.
      const nextItems = [];
      let lastWarning = '', avail = null;
      for (let ci = 0; ci < use.length; ci++) {
        if (!aliveRef.current) return;
        if (use.length > 1) setLoadMsg(`Scanning parcel area ${ci + 1} of ${use.length}…`);
        try {
          const { suggestions, warning, availableSources: as } = await detectBbox(use[ci].bbox, source);
          if (as) avail = as;
          if (warning) lastWarning = warning;
          suggestions.forEach((s) => nextItems.push({
            id:          `ai-${ci}-${nextItems.length}`,
            geometry:    s.geometry,
            include:     true,
            name:        s.name || `Field ${nextItems.length + 1}`,
            acres:       s.acres != null ? String(s.acres) : '',
            confidence:  s.confidence ?? null,
            centroidLat: s.centroidLat,
            centroidLng: s.centroidLng,
            source:      s.source || 'claude',
          }));
        } catch (clusterErr) {
          lastWarning = clusterErr.message || String(clusterErr);
        }
      }
      if (!aliveRef.current) return;
      if (avail) setAvailableSources(avail);
      if (clusters.length > MAX_CLUSTERS) {
        lastWarning = `Scanned the ${MAX_CLUSTERS} largest parcel areas of ${clusters.length}; run again from a specific farm/tract for the rest.`;
      }
      // Even with 0 results we land on review (with a note) so the user can try
      // another source or draw by hand — rather than a dead-end error screen.
      setItems(nextItems);
      setSeed(nextItems.map(it => ({ id: it.id, geometry: it.geometry })));
      setReviewNote(nextItems.length === 0
        ? (lastWarning || 'No fields detected. Try another source, or draw fields by hand below.')
        : lastWarning);
      setPhase('review');
    } catch (e) {
      if (aliveRef.current) { setErrMsg(e.message || String(e)); setPhase('error'); }
    }
  };

  useEffect(() => {
    aliveRef.current = true;
    runDetection('auto');
    return () => { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm.dealID, farm.farmID]);

  // ── Reconcile map edits (reshape / add / delete) back into the item list ─
  const onFeaturesChange = (feats) => {
    setItems(prev => {
      const prevById = new Map(prev.map(it => [it.id, it]));
      const presentIds = new Set(feats.map(f => String(f.id)));
      let counter = prev.length;
      // Keep prior items still on the map (in original order), geometry-refreshed.
      const kept = prev.filter(it => presentIds.has(String(it.id))).map(it => {
        const feat = feats.find(f => String(f.id) === String(it.id));
        return refreshGeom(it, feat.geometry);
      });
      // Append any newly hand-drawn polygons.
      const added = feats
        .filter(f => !prevById.has(String(f.id)) && !prevById.has(f.id))
        .map((f) => {
          counter += 1;
          const base = { id: f.id, include: true, name: `Field ${counter}`, confidence: null, source: 'manual' };
          return refreshGeom(base, f.geometry);
        });
      return [...kept, ...added];
    });
  };

  const setItem = (id, patch) => setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  const includedCount = items.filter(it => it.include).length;
  const totalAcres = items.filter(it => it.include).reduce((s, it) => s + (parseFloat(it.acres) || 0), 0);

  const save = async () => {
    const chosen = items.filter(it => it.include && it.geometry);
    if (chosen.length === 0) { onClose(); return; }
    setPhase('saving');
    let done = 0, failed = 0;
    for (const it of chosen) {
      setSaveMsg(`Creating field ${done + 1} of ${chosen.length}…`);
      try {
        const confBits = it.confidence != null ? ` (confidence ${Math.round(it.confidence * 100)}%)` : '';
        const detected = String(it.id).startsWith('ai-');
        const origin = it.source === 'provider' ? 'field-data provider'
          : it.source === 'model' ? 'AI field-delineation model'
          : 'Claude vision on satellite imagery';
        const notes = detected
          ? `Boundary detected via ${origin}${confBits}.`
          : 'Boundary drawn manually.';
        await api.post('fields', {
          farmId:      farm.farmID,
          name:        it.name || 'Field',
          acres:       it.acres !== '' && it.acres != null ? it.acres : null,
          status:      'Active',
          geometry:    it.geometry,
          centroidLat: it.centroidLat,
          centroidLng: it.centroidLng,
          notes,
        });
        done++;
      } catch { failed++; }
    }
    onCreated?.({ created: done, failed });
    onClose();
  };

  const title = phase === 'review'
    ? `Detected Fields — review & edit (${includedCount} selected)`
    : 'Detect Fields from Imagery';

  return (
    <Modal title={title} onClose={onClose} width={phase === 'review' ? 940 : 520}>
      {phase === 'loading' && (
        <div style={{ padding: '28px 8px', textAlign: 'center' }}>
          <Spinner />
          <div style={{ marginTop: 14, color: B.textMd, fontSize: 13 }}>
            {loadMsg || 'Capturing satellite imagery and detecting fields… this can take a moment.'}
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div>
          <div style={{ padding: 14, background: '#fdf2f2', border: `1px solid ${B.red}`, borderRadius: 8, color: B.text, fontSize: 13 }}>
            {errMsg}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <Btn variant="ghost" onClick={onClose}>Close</Btn>
          </div>
        </div>
      )}

      {phase === 'saving' && (
        <div style={{ padding: '28px 8px', textAlign: 'center' }}>
          <Spinner />
          <div style={{ marginTop: 14, color: B.textMd, fontSize: 13 }}>{saveMsg}</div>
        </div>
      )}

      {phase === 'review' && (
        <div>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>
            Each AI-detected field is a numbered <b style={{ color: '#b45309' }}>amber box</b>, clipped to your
            parcels (cyan outlines) — approximate, so review and adjust.
            <b> Click a field and drag its points to reshape it</b>, use the polygon tool (top-right) to add one,
            or the trash tool to delete. Untick any you do not want, then create the rest.
          </div>

          {/* Which detection source produced these — and re-run with another. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 12, color: B.textMd }}>
            <span style={{ fontWeight: 600 }}>Source:</span>
            {[{ key: 'auto', label: 'All available' }, ...availableSources.filter(s => s.enabled).map(s => ({ key: s.key, label: s.label }))].map(opt => {
              const active = activeSource === opt.key;
              return (
                <button key={opt.key} type="button" onClick={() => { if (!active) runDetection(opt.key); }} disabled={active}
                  title={active ? 'Currently showing this source' : `Re-run detection using ${opt.label}`}
                  style={{ padding: '3px 10px', borderRadius: 14, fontSize: 12, fontFamily: 'inherit', cursor: active ? 'default' : 'pointer',
                    border: `1px solid ${active ? B.navy : B.border}`, background: active ? B.navy : '#fff', color: active ? '#fff' : B.text }}>
                  {opt.label}
                </button>
              );
            })}
            {availableSources.filter(s => s.enabled).length <= 1 && (
              <span style={{ color: B.textLt }}>· only Claude is configured — add a provider/model to compare sources</span>
            )}
          </div>

          {reviewNote && (
            <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 12, marginBottom: 10 }}>
              {reviewNote}
            </div>
          )}

          <FieldBoundaryEditor
            parcelsFC={parcelFCRef.current}
            initialFeatures={seed}
            onFeaturesChange={onFeaturesChange}
            height={360}
          />

          <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${B.border}`, borderRadius: 8, marginTop: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: B.textMd, background: B.surfaceMuted }}>
                  <th style={{ padding: '8px 10px', width: 34 }}>
                    <input type="checkbox" checked={items.length > 0 && includedCount === items.length}
                      onChange={e => setItems(prev => prev.map(it => ({ ...it, include: e.target.checked })))} />
                  </th>
                  <th style={{ padding: '8px 10px', width: 34 }}>#</th>
                  <th style={{ padding: '8px 10px' }}>Field name</th>
                  <th style={{ padding: '8px 10px', width: 120 }}>Acres</th>
                  <th style={{ padding: '8px 10px', width: 90 }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '14px 10px', color: B.textLt }}>No fields — draw one with the polygon tool above.</td></tr>
                )}
                {items.map((it, i) => {
                  const sm = sourceMeta(it.source);
                  return (
                  <tr key={it.id} style={{ borderTop: `1px solid ${B.border}`, opacity: it.include ? 1 : 0.5 }}>
                    <td style={{ padding: '4px 10px' }}>
                      <input type="checkbox" checked={!!it.include} onChange={e => setItem(it.id, { include: e.target.checked })} />
                    </td>
                    <td style={{ padding: '4px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 11, background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700 }}>{i + 1}</span>
                    </td>
                    <td style={{ padding: '4px 10px' }}><Input value={it.name} onChange={v => setItem(it.id, { name: v })} /></td>
                    <td style={{ padding: '4px 10px' }}><Input type="number" value={it.acres} onChange={v => setItem(it.id, { acres: v })} /></td>
                    <td style={{ padding: '4px 10px' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#fff', background: sm.color }}>{sm.label}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
            <span style={{ fontSize: 12, color: B.textMd }}>
              {items.length} field{items.length === 1 ? '' : 's'} · {includedCount} selected · {num(totalAcres)} ac
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
              <Btn onClick={save} disabled={includedCount === 0}>Create {includedCount} field{includedCount === 1 ? '' : 's'}</Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

// Recompute acres / centroid from a (possibly edited) geometry.
function refreshGeom(item, geometry) {
  const centroid = polygonCentroid(geometry);
  return {
    ...item,
    geometry,
    acres:       acresStr(geometry),
    centroidLat: centroid ? centroid.lat : null,
    centroidLng: centroid ? centroid.lng : null,
  };
}
